import { useEffect, useState } from "react";
import _ from "lodash";
import axios from "axios";
import { useIntl } from "react-intl";
import { useTheme } from "@emotion/react";
import { Box } from "@mui/material";
import { useOutletContext } from "react-router";
import { formatDate, getShortAddress } from "features/helpers/utils";
import { apiUrl } from "features/configure";
import { getUserAvatarIcon } from "features/metadesk/utils";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import useNavigateEntity from "hooks/useNavigateEntity";
import CRMTable from "@metacrm/metacrm-material-ui/dist/Table";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import {
  StyledUserTag,
  StyledDot,
  StyledTooltipRow,
} from "./TopMemberContent.styles";

export function TopMemberAnalytics({
  startDate,
  endDate,
  platform,
  excludedUsers,
}) {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const [entityName] = useOutletContext();
  const { navigateEntity } = useNavigateEntity();
  const [topMembers, setTopMembers] = useState();
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("totalMessages");
  const [loading, setLoading] = useState(false);
  const dispatchSnackbar = useDispatchSnackbar();

  const userTagMap = {
    member: {
      label: formatMessage({
        id: "cummunityAnalysis.topMember.userTag.member",
      }),
      bgColor: "#D3F4E1",
    },
    bot: {
      label: formatMessage({ id: "cummunityAnalysis.topMember.userTag.bot" }),
      bgColor: "#D3E2FC",
    },
    suspected: {
      label: formatMessage({
        id: "cummunityAnalysis.topMember.userTag.suspected",
      }),
      bgColor: "#FFDED7",
      txtColor: "#FC5555",
    },
    left: {
      label: formatMessage({ id: "cummunityAnalysis.topMember.userTag.left" }),
      bgColor: "#FAF6FF",
    },
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const rowData = _.get(topMembers, "data", []);

  useEffect(() => {
    fetchTopMembersData();
  }, [startDate, endDate, platform, page, order, orderBy, excludedUsers]);

  const fetchTopMembersData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${apiUrl}/api/communityDashboard/${entityName}/${platform}/topMembers`,
        {
          startDate,
          endDate,
          page,
          sort: orderBy,
          order: order === "asc" ? 1 : -1,
        }
      );
      setTopMembers(data);
    } catch (err) {
      dispatchSnackbar({ err });
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    {
      show: true,
      disabledSort: true,
      id: "avatar",
      label: formatMessage({ id: "global.header.avatar" }),
      width: 50,
    },
    {
      show: true,
      disabledSort: true,
      id: "userName",
      label: formatMessage({ id: "global.header.userName" }),
      width: 150,
    },
    {
      show: true,
      disabledSort: true,
      id: "userId",
      label: formatMessage({ id: "global.header.userID" }),
      width: 150,
    },
    {
      show: true,
      disabledSort: true,
      id: "addressCollection",
      label: (
        <Box className="startRow">
          <Box className="mgr4">
            {formatMessage({ id: "global.property.addressCollection" })}
          </Box>
          <IconTooltip
            content={formatMessage({
              id: "cummunityAnalysis.topMember.addressCollection.tooltip",
            })}
          />
        </Box>
      ),
      width: 200,
    },
    {
      show: true,
      id: "totalMessages",
      label: formatMessage({ id: "global.header.message" }),
      width: 150,
    },
    {
      show: true,
      id: "totalReactions",
      label: formatMessage({ id: "global.header.reaction" }),
      width: 150,
    },
    {
      show: true,
      disabledSort: true,
      id: "userTag",
      label: formatMessage({ id: "global.property.userTag" }),
      width: 150,
    },
    {
      show: true,
      id: "lastActive",
      label: formatMessage({ id: "global.header.lastActivityTime" }),
    },
  ];

  const renderContents = (member) => {
    const contents = [
      <Box slot="avatar">
        <Box className="startRow">
          <img
            src={
              member?.avatar
                ? member.avatar
                : require(`assets/img/${getUserAvatarIcon(
                    member.userId,
                    theme
                  )}`)
            }
            className="icon"
            style={{ borderRadius: 20, marginRight: 8, width: 20, height: 20 }}
          />
        </Box>
      </Box>,
      <Box slot="userName">{member?.userName || "-"}</Box>,
      <Box slot="userId">{`@${member?.userId}` || "-"}</Box>,
      <Box slot="addressCollection">
        {member.addressCollection ? (
          <Box
            className="startRow"
            sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={require(`assets/img/ic_premium.svg`).default}
              style={{ marginRight: 4 }}
            />
            <div
              style={{
                color: theme.customColors.purple[500],
                borderBottom: `1px solid ${theme.customColors.purple[500]}`,
                cursor: "pointer",
                lineHeight: "14px",
              }}
              onClick={() => {
                navigateEntity(
                  `/customers/list?userId=${member.addressCollection.userId}`
                );
              }}>
              {getShortAddress(_.get(member, "addressCollection.address", ""))}
            </div>
          </Box>
        ) : (
          <div style={{ color: theme.customColors.grey[500] }}>{"N/A"}</div>
        )}
      </Box>,
      <Box slot="totalMessages">
        {member.totalMessages ? member.totalMessages : "-"}
      </Box>,
      <Box slot="totalReactions">
        {member.totalReactions ? member.totalReactions : "-"}
      </Box>,
      <Box slot="userTag">
        <div>
          {member.userTag ? (
            <StyledUserTag
              bgColor={userTagMap[member.userTag].bgColor}
              textColor={userTagMap[member.userTag].txtColor}>
              {member.userTag !== "suspected" ? (
                <>{userTagMap[member.userTag].label}</>
              ) : (
                <IconTooltip
                  icon={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "4px",
                      }}>
                      <img
                        src={require("assets/img/ic_xShield.svg").default}
                        alt=""
                        style={{ position: "relative", bottom: "1px" }}
                      />
                      <span
                        style={{
                          color: userTagMap[member.userTag].txtColor,
                          fontSize: "10px",
                          fontWeight: 500,
                        }}>
                        {userTagMap[member.userTag].label}
                      </span>
                    </Box>
                  }
                  content={
                    <Box fontWeight={500} fontSize={"12px"}>
                      <StyledTooltipRow
                        style={{
                          marginBottom: "2px",
                        }}>
                        <StyledDot />
                        {"Message sending interval is too regular"}
                      </StyledTooltipRow>
                      <StyledTooltipRow>
                        <StyledDot />
                        {"Frequent use of Bot commands"}
                      </StyledTooltipRow>
                    </Box>
                  }
                />
              )}
            </StyledUserTag>
          ) : (
            "-"
          )}
        </div>
      </Box>,
      <Box slot="lastActive">
        {formatDate(_.get(member, "lastActive", ""), { time: true })}{" "}
      </Box>,
    ];
    return contents;
  };

  return (
    <Box position={"relative"} sx={{ mt: "12px" }}>
      <CRMTable
        headers={headers}
        rowData={rowData}
        onRequestSort={handleRequestSort}
        order={order}
        orderBy={orderBy}
        page={page}
        dataCounts={topMembers?.total || ""}
        contents={_.map(rowData, (member) => renderContents(member))}
        noDataComponent={
          <Box>
            <EmptyIconStatus
              icon="table"
              emptyTitle={formatMessage({ id: "emptyState.table.title" })}
              emptyDescription={formatMessage({
                id: "emptyState.table.description",
              })}
            />
          </Box>
        }
        isLoading={loading}
        setPage={(v) => setPage(v)}
      />
    </Box>
  );
}
